import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import DietPlanSystem from './DietPlanSystem';
import DietPlansList from './DietPlansList';

/* ── Helpers ─────────────────────────────────────── */
const CUISINES = ['Indian', 'Italian', 'Chinese', 'Mexican', 'Mediterranean', 'Japanese', 'Thai', 'French'];

const CUISINE_EMOJI = {
  Indian: '🇮🇳', Italian: '🇮🇹', Chinese: '🇨🇳', Mexican: '🇲🇽',
  Mediterranean: '🫒', Japanese: '🇯🇵', Thai: '🇹🇭', French: '🇫🇷',
};

function CheckboxPill({ label, checked, onChange }) {
  return (
    <label
      className={`checkbox-item ${checked ? 'checked' : ''}`}
      onClick={(e) => { e.preventDefault(); onChange(); }}
    >
      <input type="checkbox" checked={checked} onChange={() => {}} />
      {checked ? '✓ ' : ''}{label}
    </label>
  );
}

function DietControl({ onBack, userName = '', initialView = false, onViewAllPlans }) {
  const [dietPlanData, setDietPlanData] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [submitError, setSubmitError]   = useState('');
  const isSubmitting = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const showAllPlans = initialView || new URLSearchParams(location.search).get('view') === 'all';

  const [form, setForm] = useState({
    name: userName, age: '', gender: 'male',
    height: '', heightUnit: 'ft',
    currentWeight: '', goalWeight: '', weightUnit: 'kg',
    activityLevel: 'moderate', occupation: 'sedentary',
    vegetarian: false, vegan: false, glutenFree: false, nonVegetarian: false, pescatarian: false,
    allergies: '', restrictions: '',
    cuisinePreferences: [], dislikedFoods: '',
    cookingSkillLevel: 'intermediate', budgetLevel: 'moderate',
    diabetes: false, hypertension: false, cholesterol: false, otherConditions: '',
  });

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleCuisine = (c) => {
    setForm(prev => ({
      ...prev,
      cuisinePreferences: prev.cuisinePreferences.includes(c)
        ? prev.cuisinePreferences.filter(x => x !== c)
        : [...prev.cuisinePreferences, c],
    }));
  };

  const getBMI = () => {
    if (!form.currentWeight || !form.height) return { score: '—', label: 'Enter metrics', color: 'var(--color-text-muted)' };
    const h = form.heightUnit === 'cm' ? form.height / 100 : form.height * 0.3048;
    const w = form.weightUnit === 'kg' ? form.currentWeight : form.currentWeight * 0.453592;
    const score = parseFloat((w / (h * h)).toFixed(1));
    if (score < 18.5) return { score, label: 'Underweight',  color: '#3B82F6' };
    if (score < 25)   return { score, label: 'Healthy ✓',   color: '#16A34A' };
    if (score < 30)   return { score, label: 'Overweight',   color: '#F59E0B' };
    return              { score, label: 'Obese',           color: '#EF4444' };
  };

  const bmi = getBMI();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting.current) return;

    isSubmitting.current = true;
    setLoading(true);
    setSubmitError('');
    const payload = {
      user_profile: {
        full_name: form.name,
        age: parseInt(form.age),
        gender: form.gender,
        physical_metrics: {
          height:         { value: parseFloat(form.height),        unit: form.heightUnit },
          current_weight: { value: parseFloat(form.currentWeight), unit: form.weightUnit },
          goal_weight:    { value: parseFloat(form.goalWeight),     unit: form.weightUnit },
          bmi_data:       { score: bmi.score, category: bmi.label },
        },
      },
      lifestyle: {
        activity_level: form.activityLevel,
        occupation: form.occupation,
        practical: {
          cooking_skill: form.cookingSkillLevel,
          budget: form.budgetLevel,
        },
      },
      dietary_constraints: {
        base_preferences: {
          is_vegetarian: form.vegetarian,
          is_vegan: form.vegan,
          is_gluten_free: form.glutenFree,
          is_non_vegetarian: form.nonVegetarian,
          is_pescatarian: form.pescatarian,
        },
        cuisines: form.cuisinePreferences,
        exclusions: {
          allergies:    form.allergies.split(',').map(s => s.trim()).filter(Boolean),
          restrictions: form.restrictions.split(',').map(s => s.trim()).filter(Boolean),
          disliked_foods: form.dislikedFoods.split(',').map(s => s.trim()).filter(Boolean),
        },
      },
      health_and_macros: {
        conditions: {
          diabetes: form.diabetes,
          hypertension: form.hypertension,
          cholesterol: form.cholesterol,
          notes: form.otherConditions,
        },
      },
    };
    try {
      const response = await api.post('/dietplan', payload);
      setDietPlanData(response.data);
    } catch (err) {
      console.error('Error creating diet plan:', err.response?.data || err.message);
      setSubmitError('We couldn’t create your meal plan right now. Please try again.');
    } finally {
      isSubmitting.current = false;
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '2px solid var(--color-border)',
    borderRadius: 'var(--radius-md)',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '0.92rem',
    color: 'var(--color-text)',
    background: 'var(--color-card)',
    outline: 'none',
    transition: 'border-color 0.15s',
    boxSizing: 'border-box',
  };

  if (showAllPlans) {
    return <DietPlansList onBack={() => navigate('/diet')} />;
  }

  if (dietPlanData) {
    return (
      <div className="content-panel-wide">
        <button className="back-btn" onClick={() => setDietPlanData(null)}>
          &#8592; Back to Form
        </button>
        <DietPlanSystem data={dietPlanData} />
      </div>
    );
  }

  return (
    <div className="content-panel diet-page">
      <button className="back-btn" onClick={onBack}>&#8592; Back</button>

      <section className="diet-welcome">
        <img
          src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&w=1100&q=80"
          alt="A balanced meal with fresh vegetables, grains and greens"
          width="1100"
          height="700"
        />
        <div className="diet-welcome-copy">
          <span className="diet-premium-label">A more personal way to eat well</span>
          <h1>Your goals. Your routine. Your plate.</h1>
          <p>Build a thoughtful 7-day meal plan around your lifestyle, food preferences, and wellness goals.</p>
          <span className="diet-membership-note">A new personalised membership experience is on the way</span>
        </div>
      </section>

      <div className="section-header">
        <h2>Create your personal meal plan</h2>
        <p>Tell us a little about yourself to shape your personalised 7-day plan.</p>
      </div>

      {submitError && (
        <div className="diet-submit-error" role="alert">
          {submitError}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── 1. Personal Information ── */}
        <div className="form-section-card">
          <div className="form-section-header">
            <div className="form-section-icon">👤</div>
            <h3 className="form-section-title">Personal Information</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row-custom">
              <div className="form-group-custom" style={{ flex: 2 }}>
                <label className="form-label-custom">Full Name <span className="required-star">*</span></label>
                <input style={inputStyle} type="text" name="name" value={form.name} onChange={set} required placeholder="Your name" />
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Age <span className="required-star">*</span></label>
                <input style={inputStyle} type="number" name="age" value={form.age} onChange={set} required min="1" max="120" placeholder="Years" />
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Gender <span className="required-star">*</span></label>
                <select style={inputStyle} name="gender" value={form.gender} onChange={set}>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row-custom">
              <div className="form-group-custom">
                <label className="form-label-custom">Height <span className="required-star">*</span></label>
                <div style={{ display: 'flex', gap: 0 }}>
                  <input
                    style={{ ...inputStyle, borderRadius: 'var(--radius-md) 0 0 var(--radius-md)', flex: 2 }}
                    type="number" name="height" value={form.height} onChange={set} required step="0.1"
                    placeholder={form.heightUnit === 'ft' ? 'e.g. 5.5' : 'e.g. 168'}
                  />
                  <select
                    style={{ ...inputStyle, borderRadius: '0 var(--radius-md) var(--radius-md) 0', flex: 1, borderLeft: 'none', background: 'var(--color-bg)' }}
                    name="heightUnit" value={form.heightUnit} onChange={set}
                  >
                    <option value="cm">cm</option>
                    <option value="ft">ft</option>
                  </select>
                </div>
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Current Weight <span className="required-star">*</span></label>
                <div style={{ display: 'flex' }}>
                  <input
                    style={{ ...inputStyle, borderRadius: 'var(--radius-md) 0 0 var(--radius-md)', flex: 2 }}
                    type="number" name="currentWeight" value={form.currentWeight} onChange={set} required step="0.1" placeholder="Value"
                  />
                  <select
                    style={{ ...inputStyle, borderRadius: '0 var(--radius-md) var(--radius-md) 0', flex: 1, borderLeft: 'none', background: 'var(--color-bg)' }}
                    name="weightUnit" value={form.weightUnit} onChange={set}
                  >
                    <option value="kg">kg</option>
                    <option value="lbs">lbs</option>
                  </select>
                </div>
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Goal Weight <span className="required-star">*</span></label>
                <input style={inputStyle} type="number" name="goalWeight" value={form.goalWeight} onChange={set} required step="0.1" placeholder={`in ${form.weightUnit}`} />
              </div>
            </div>

            {/* BMI Display */}
            <div className="bmi-display" style={{ borderColor: bmi.color, background: `${bmi.color}10` }}>
              <div>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                  Your BMI
                </div>
                <div className="bmi-score" style={{ color: bmi.color }}>{bmi.score}</div>
              </div>
              <div className="bmi-label" style={{ color: bmi.color }}>{bmi.label}</div>
            </div>
          </div>
        </div>

        {/* ── 2. Lifestyle ── */}
        <div className="form-section-card">
          <div className="form-section-header">
            <div className="form-section-icon">🏃</div>
            <h3 className="form-section-title">Lifestyle</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row-custom">
              <div className="form-group-custom">
                <label className="form-label-custom">Activity Level <span className="required-star">*</span></label>
                <select style={inputStyle} name="activityLevel" value={form.activityLevel} onChange={set}>
                  <option value="sedentary">Sedentary (little exercise)</option>
                  <option value="light">Light (1–3 days/week)</option>
                  <option value="moderate">Moderate (3–5 days/week)</option>
                  <option value="active">Active (6–7 days/week)</option>
                  <option value="veryActive">Very Active (intense daily)</option>
                </select>
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Occupation <span className="required-star">*</span></label>
                <select style={inputStyle} name="occupation" value={form.occupation} onChange={set}>
                  <option value="sedentary">Sedentary (desk job)</option>
                  <option value="light">Light (standing job)</option>
                  <option value="moderate">Moderate (physically demanding)</option>
                  <option value="heavy">Heavy Labour</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── 3. Dietary Preferences ── */}
        <div className="form-section-card">
          <div className="form-section-header">
            <div className="form-section-icon">🥗</div>
            <h3 className="form-section-title">Dietary Preferences</h3>
          </div>
          <div className="form-section-body">
            <label className="form-label-custom" style={{ display: 'block', marginBottom: '10px' }}>Dietary Style</label>
            <div className="checkbox-group" style={{ marginBottom: '20px' }}>
              <CheckboxPill label="Vegetarian"     checked={form.vegetarian}    onChange={() => setForm(p => ({...p, vegetarian: !p.vegetarian}))} />
              <CheckboxPill label="Vegan"          checked={form.vegan}         onChange={() => setForm(p => ({...p, vegan: !p.vegan}))} />
              <CheckboxPill label="Non-Vegetarian" checked={form.nonVegetarian} onChange={() => setForm(p => ({...p, nonVegetarian: !p.nonVegetarian}))} />
              <CheckboxPill label="Pescatarian"    checked={form.pescatarian}   onChange={() => setForm(p => ({...p, pescatarian: !p.pescatarian}))} />
              <CheckboxPill label="Gluten Free"    checked={form.glutenFree}    onChange={() => setForm(p => ({...p, glutenFree: !p.glutenFree}))} />
            </div>

            <div className="form-row-custom">
              <div className="form-group-custom">
                <label className="form-label-custom">Allergies</label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                  name="allergies" value={form.allergies} onChange={set}
                  placeholder="Nuts, shellfish, dairy… (comma-separated)"
                  rows="2"
                />
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Dietary Restrictions</label>
                <textarea
                  style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                  name="restrictions" value={form.restrictions} onChange={set}
                  placeholder="Foods to avoid… (comma-separated)"
                  rows="2"
                />
              </div>
            </div>

            <div className="form-group-custom" style={{ marginBottom: '16px' }}>
              <label className="form-label-custom">Cuisine Preferences</label>
              <div className="cuisine-grid">
                {CUISINES.map(c => (
                  <label
                    key={c}
                    className={`cuisine-chip ${form.cuisinePreferences.includes(c) ? 'selected' : ''}`}
                    onClick={(e) => { e.preventDefault(); toggleCuisine(c); }}
                  >
                    <input type="checkbox" checked={form.cuisinePreferences.includes(c)} onChange={() => {}} />
                    <span className="cuisine-flag" role="img" aria-label={`${c} cuisine flag`}>
                      {CUISINE_EMOJI[c]}
                    </span>
                    {c}
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group-custom">
              <label className="form-label-custom">Disliked Foods</label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                name="dislikedFoods" value={form.dislikedFoods} onChange={set}
                placeholder="Foods you prefer not to eat… (comma-separated)"
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* ── 4. Health Conditions ── */}
        <div className="form-section-card">
          <div className="form-section-header">
            <div className="form-section-icon">❤️</div>
            <h3 className="form-section-title">Health Conditions</h3>
          </div>
          <div className="form-section-body">
            <div className="checkbox-group" style={{ marginBottom: '20px' }}>
              <CheckboxPill label="Diabetes"         checked={form.diabetes}     onChange={() => setForm(p => ({...p, diabetes: !p.diabetes}))} />
              <CheckboxPill label="Hypertension"     checked={form.hypertension} onChange={() => setForm(p => ({...p, hypertension: !p.hypertension}))} />
              <CheckboxPill label="High Cholesterol" checked={form.cholesterol}  onChange={() => setForm(p => ({...p, cholesterol: !p.cholesterol}))} />
            </div>
            <div className="form-group-custom">
              <label className="form-label-custom">Other Conditions</label>
              <textarea
                style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                name="otherConditions" value={form.otherConditions} onChange={set}
                placeholder="List any other health conditions…"
                rows="2"
              />
            </div>
          </div>
        </div>

        {/* ── 5. Practical Preferences ── */}
        <div className="form-section-card">
          <div className="form-section-header">
            <div className="form-section-icon">⚙️</div>
            <h3 className="form-section-title">Practical Preferences</h3>
          </div>
          <div className="form-section-body">
            <div className="form-row-custom">
              <div className="form-group-custom">
                <label className="form-label-custom">Cooking Skill <span className="required-star">*</span></label>
                <select style={inputStyle} name="cookingSkillLevel" value={form.cookingSkillLevel} onChange={set}>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div className="form-group-custom">
                <label className="form-label-custom">Budget Level <span className="required-star">*</span></label>
                <select style={inputStyle} name="budgetLevel" value={form.budgetLevel} onChange={set}>
                  <option value="low">Low</option>
                  <option value="moderate">Moderate</option>
                  <option value="high">High</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* ── Submit ── */}
        <div className="diet-form-actions">
          <button
            type="submit"
            className="btn-primary-custom"
            disabled={loading}
            style={loading ? { opacity: 0.65, cursor: 'not-allowed', transform: 'none' } : {}}
          >
            {loading ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                Generating Plan…
              </>
            ) : (
              '🥗 Create My Diet Plan'
            )}
          </button>
          <button type="button"
            className="btn-primary-custom"
            disabled={loading}
            onClick={onViewAllPlans || (() => navigate('/diet?view=all'))}
            style={loading ? { opacity: 0.65, cursor: 'not-allowed', transform: 'none' } : {}}>
            🥗 View All Diet Plans
          </button>
          <button type="button" className="btn-secondary-custom" onClick={onBack}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default DietControl;
